/**
 * Ask Agent Loop, the AI helper (Oct 2026). Laid out like "Ask Granted":
 * a full page in the main area, the greeting on the left, suggested
 * questions on the right as ready-made replies, and the message box
 * pinned to the bottom. The conversation itself lives in AskProvider.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUp, Check, Copy, Loader2, SquarePen, X } from 'lucide-react';
import { useAsk } from '../lib/ask';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { updateIntercom } from '../lib/intercom';
import '@styles/pages/ask.css';

const STARTERS = [
  'Where are my cases at?',
  'What does a code on my bill mean?',
  'Should I pay my bill while you work on it?',
  "What if you can't lower my bill?",
];

/* Just enough formatting for short answers: paragraphs, "- " lists and
   **bold**. Anything else shows as plain text. */
function inline(text: string, key: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={`${key}-${i}`}>{part}</span>
    ),
  );
}

function Answer({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((block, bi) => {
        const lines = block.split('\n').filter((l) => l.trim());
        if (lines.length && lines.every((l) => /^\s*[-*•]\s+/.test(l))) {
          return (
            <ul key={bi}>
              {lines.map((l, li) => (
                <li key={li}>{inline(l.replace(/^\s*[-*•]\s+/, ''), `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        return <p key={bi}>{inline(lines.join(' '), `${bi}`)}</p>;
      })}
    </>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="ak-tool"
      title="Copy"
      aria-label="Copy answer"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard blocked */
        }
      }}
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

export default function Ask() {
  const { user } = useAuth();
  const { msgs, busy, caseNumber, setCaseNumber, send, reset, talkToPerson } = useAsk();
  const [params] = useSearchParams();
  const [input, setInput] = useState('');
  const [firstName, setFirstName] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /* /ask?case=1042 from an email or a link focuses that case. */
  useEffect(() => {
    const n = Number(params.get('case'));
    if (Number.isInteger(n) && n > 0) setCaseNumber(n);
  }, [params, setCaseNumber]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('first_name')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const n = (data?.first_name as string | undefined) ?? '';
        if (n) setFirstName(n.charAt(0).toUpperCase() + n.slice(1));
      });
  }, [user]);

  /* Intercom's bubble sits on top of the send button, so it hides here. */
  useEffect(() => {
    updateIntercom({ hide_default_launcher: true });
    return () => updateIntercom({ hide_default_launcher: false });
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = (q: string) => {
    if (!q.trim() || busy) return;
    send(q);
    setInput('');
  };

  const starters = caseNumber ? [`What's happening with case #${caseNumber}?`, ...STARTERS.slice(1)] : STARTERS;

  return (
    <div className="ak">
      <header className="ak-top">
        <h1 className="ak-title">Ask Agent Loop</h1>
        {caseNumber != null && (
          <span className="ak-focus">
            About case #{caseNumber}
            <button type="button" onClick={() => setCaseNumber(null)} aria-label="Stop focusing on this case">
              <X size={12} />
            </button>
          </span>
        )}
        <span className="ak-top-spacer" />
        {msgs.length > 0 && (
          <button type="button" className="ak-new" onClick={reset}>
            <SquarePen size={15} /> New chat
          </button>
        )}
      </header>

      <div className="ak-scroll">
        <div className="ak-col">
          <div className="ak-ai">
            <p className="ak-hello">
              {firstName ? `Hi ${firstName}! ` : 'Hi there! '}What can we help you with today?
            </p>
            <p className="ak-hint">
              Ask about your cases or anything on a medical bill. Your specialist still does all
              the work with your provider.
            </p>
          </div>

          {msgs.length === 0 ? (
            <div className="ak-starters">
              {starters.map((s) => (
                <button key={s} type="button" className="ak-starter" onClick={() => submit(s)}>
                  {s}
                </button>
              ))}
            </div>
          ) : (
            msgs.map((m, i) =>
              m.role === 'user' ? (
                <div key={i} className="ak-me">
                  <div className="ak-bubble">{m.content}</div>
                </div>
              ) : (
                <div key={i} className="ak-ai">
                  {m.content && (
                    <div className="ak-answer">
                      <Answer text={m.content} />
                    </div>
                  )}
                  {m.streaming && (m.status || !m.content) && (
                    <p className="ak-status">
                      <Loader2 size={14} className="ak-spin" /> {m.status ?? 'Thinking'}…
                    </p>
                  )}
                  {m.error && <p className="ak-error">{m.error}</p>}
                  {!m.streaming && m.content && (
                    <div className="ak-tools">
                      <CopyButton text={m.content} />
                    </div>
                  )}
                  {!m.streaming && m.handoff && (
                    <div className="ak-card">
                      <p className="ak-card-title">Want a person to take a look?</p>
                      <p className="ak-card-body">
                        We'll open a message to the mediloop team with your question already written.
                      </p>
                      <button type="button" className="ml-btn ml-btn--ink ak-card-btn" onClick={() => talkToPerson(m.handoff)}>
                        Talk to a person
                      </button>
                    </div>
                  )}
                </div>
              ),
            )
          )}
          <div ref={endRef} />
        </div>
      </div>

      <form
        className="ak-foot"
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
      >
        <div className="ak-compose">
          <textarea
            ref={inputRef}
            className="ak-input"
            rows={1}
            placeholder="Ask about a case or a bill…"
            value={input}
            maxLength={2000}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
          />
          <button type="submit" className="ak-send" disabled={busy || !input.trim()} aria-label="Send">
            {busy ? <Loader2 size={16} className="ak-spin" /> : <ArrowUp size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}
